import { z } from "zod";
import { CANVAS_FONT_FAMILIES } from "../canvas/canvasFonts";
import {
  CANVAS_AI_LIMITS,
  CANVAS_AI_MAX_OPERATIONS,
} from "./canvasAi.constants";

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const REFERENCE_PATTERN = /^[a-z0-9_-]+$/i;
const coordinateSchema = z
  .number()
  .finite()
  .min(-CANVAS_AI_LIMITS.coordinate)
  .max(CANVAS_AI_LIMITS.coordinate);
const dimensionSchema = z
  .number()
  .finite()
  .min(CANVAS_AI_LIMITS.sizeMin)
  .max(CANVAS_AI_LIMITS.sizeMax);
const textSchema = z.string().max(CANVAS_AI_LIMITS.textLength);
const idSchema = z.string().min(1).max(128);
const referenceSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(REFERENCE_PATTERN, "References use letters, numbers, dashes, or underscores");
const hexColorSchema = z.string().regex(HEX_COLOR_PATTERN, "Use a six-digit hex color");
const fontFamilySchema = z.enum(CANVAS_FONT_FAMILIES.map(({ id }) => id));

export const canvasAiStyleSchema = z.object({
  fill: z.union([hexColorSchema, z.literal("transparent")]).optional(),
  fontFamily: fontFamilySchema.optional(),
  fontSize: z
    .number()
    .finite()
    .min(CANVAS_AI_LIMITS.fontSizeMin)
    .max(CANVAS_AI_LIMITS.fontSizeMax)
    .optional(),
  fontStyle: z.enum(["normal", "italic"]).optional(),
  fontWeight: z.union([z.literal(400), z.literal(600), z.literal(700)]).optional(),
  stroke: hexColorSchema.optional(),
  strokeWidth: z
    .number()
    .finite()
    .min(CANVAS_AI_LIMITS.strokeWidthMin)
    .max(CANVAS_AI_LIMITS.strokeWidthMax)
    .optional(),
});

export const canvasAiEndpointSchema = z.union([
  z.object({ elementRef: idSchema }),
  z.object({ x: coordinateSchema, y: coordinateSchema }),
]);

const shapeElementSchema = z.object({
  height: dimensionSchema,
  label: textSchema.optional(),
  style: canvasAiStyleSchema.optional(),
  type: z.enum(["rectangle", "diamond", "ellipse"]),
  width: dimensionSchema,
  x: coordinateSchema,
  y: coordinateSchema,
});

const textElementSchema = z.object({
  style: canvasAiStyleSchema.optional(),
  text: textSchema,
  type: z.literal("text"),
  x: coordinateSchema,
  y: coordinateSchema,
});

const arrowElementSchema = z.object({
  end: canvasAiEndpointSchema,
  start: canvasAiEndpointSchema,
  style: canvasAiStyleSchema.optional(),
  type: z.literal("arrow"),
});

const addOperationSchema = z.object({
  element: z.discriminatedUnion("type", [
    shapeElementSchema,
    textElementSchema,
    arrowElementSchema,
  ]),
  kind: z.literal("add"),
  ref: referenceSchema,
});

const updateChangesSchema = z
  .object({
    end: canvasAiEndpointSchema.optional(),
    height: dimensionSchema.optional(),
    label: textSchema.optional(),
    start: canvasAiEndpointSchema.optional(),
    style: canvasAiStyleSchema.optional(),
    text: textSchema.optional(),
    width: dimensionSchema.optional(),
    x: coordinateSchema.optional(),
    y: coordinateSchema.optional(),
  })
  .refine((changes) => Object.keys(changes).length > 0, "Update needs a change");

const updateOperationSchema = z.object({
  changes: updateChangesSchema,
  id: idSchema,
  kind: z.literal("update"),
});

const deleteOperationSchema = z.object({
  id: idSchema,
  kind: z.literal("delete"),
});

export const canvasAiPatchSchema = z.object({
  operations: z
    .array(
      z.discriminatedUnion("kind", [
        addOperationSchema,
        updateOperationSchema,
        deleteOperationSchema,
      ])
    )
    .min(1)
    .max(CANVAS_AI_MAX_OPERATIONS),
  summary: z.string().min(1).max(200),
});
